/* eslint react/sort-comp: 0 */
import React from "react";
import { ElementsConsumer } from "@stripe/react-stripe-js";
import PropTypes from "prop-types";
import { mapObject } from "underscore";

import DonationForm from "containers/DonationForm";
import i18n from "lib/i18n";
import Presets from "lib/Presets";
import PayPalManager from "lib/PayPalManager";
import StripeManager from "lib/StripeManager";
import TcDonateGateway from "lib/TcDonateGateway";
import { ConfigProps, ConfigDefaultProps } from "components/PropTypes/ConfigProps";

import Validation from "./Validation";

const REGIONS_WITH_ADDRESS = ["en-GB", "en-US"];
const REGIONS_WITH_OPTIONAL_ADDRESS = [];
const VALIDATIONS_DELAY = 1000;

const initialAmount = (presets, config, frequency) => {
  const validAmounts = config.suggested_amounts_in_dollars[frequency];
  const validPresetAmount = validAmounts.find(e => e === presets.amount());

  return validPresetAmount || config.default_amounts_in_dollars[frequency];
};

const initialFrequency = (presets, config) => {
  const validFrequencies = config.frequency_allowed_values;
  const validPresetFrequency = validFrequencies.find(e => e === presets.frequency());

  return validPresetFrequency || config.default_frequency;
};

export class UnwrappedDonationFormManager extends React.Component {
  constructor(props) {
    super(props);

    const frequency = initialFrequency(props.presets, props.config);
    const amount = initialAmount(props.presets, props.config, frequency);

    this.recaptchaRef = React.createRef();
    this.state = {
      address: "",
      amount,
      cardNumber: false,
      city: "",
      country: "",
      countryCode: undefined,
      cvv: false,
      email: "",
      errors: {},
      expiry: false,
      frequency,
      giftAid: false,
      lastName: "",
      name: "",
      paymentProvider: props.stripeManager.available ? "credit_card" : "paypal",
      postcode: "",
      publiclyRecogniseDonor: false,
      recaptchaToken: "",
      state: "",
      submitting: false,
      title: "",
      valid: false,
      // As we update, we run the validations, populating state.errors. We only
      // show the errors once we attempt payment (i.e. state.showErrors is true).
      // Once this happens, we'll be pushing down live error info to
      // DonationForm.
      showErrors: false,
      loading: false,
    };
    this.showAddressInputs = REGIONS_WITH_ADDRESS.includes(this.props.config.locale);
    this.showOptionalAddress = REGIONS_WITH_OPTIONAL_ADDRESS.includes(this.props.config.locale);

    this.validation = new Validation({
      locale: this.props.config.locale,
      minAmount: this.props.config.amount_min,
      maxAmount: this.props.config.amount_max,
      validateAddresses: this.showAddressInputs,
    });

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.onPaypalClick = this.onPaypalClick.bind(this);
    this.loading = this.loading.bind(this);
    this.succeed = this.succeed.bind(this);
    this.setRecaptchaToken = this.setRecaptchaToken.bind(this);
    this.initialFrequency = frequency;

    // Initialise errors
    this.state.validating = true;
    this.validate(this.state);

    // Initialise pending validations (for batch processing)
    this.pendingValidations = {};
  }

  /**
   * Overridden React lifecycle method -
   * https://reactjs.org/docs/react-component.html#componentdidupdate
   *
   * Checks any change in state to see if we started or stopped submitting, and
   * disables or enables Stripe elements (credit card field, etc.) as appropriate
   * @param prevProps
   * @param prevState
   */
  componentDidUpdate(prevProps, prevState) {
    if (this.props.stripeManager && prevState.submitting !== this.state.submitting) {
      this.props.stripeManager.enableElements(!this.state.submitting);
    }
  }

  setRecaptchaToken(recaptchaToken) {
    this.recaptchaRef.current.reset();
    this.setState(
      { recaptchaToken },
      () => this.props.stripeManager.executePayment()
    );
  }

  /**
   * An external interface to allow payment managers to know the currently
   * selected payment frequency
   */
  get frequency() {
    return this.state.frequency;
  }

  handleSubmit(event) {
    event.preventDefault();
    UnwrappedDonationFormManager.preventDoubleClick(event);

    this.setState({ showErrors: true, submitting: this.state.valid }, () => {
      if (this.state.valid) {
        this.recaptchaRef.current.execute();
      }
    });
  }

  /**
   * Setting `state.submitting` to true in `handleSubmit` will result in the
   * form's submit button being disabled, but maybe not in time to prevent the
   * donor double-clicking it. This would cause attempted double submission of
   * the same payment intent (_not double payment_), causing an ephemeral error
   * message in the UI and a Bugsnag error. Hence, we use standard DOM
   * manipulation here to ensure the button is disabled right away, making it
   * impossible to double-click. Then, setting `state.submitting`
   * to true or false in `handleSubmit` will ensure that it can be clicked
   * again if that makes sense.
   * @see https://app.bugsnag.com/the-conversation/tc-donations/errors/60176941205f860018a4f947
   */
  static preventDoubleClick(event) {
    // eslint-disable-next-line no-param-reassign
    event.target.commit.disabled = true;
  }

  /**
   * Visually indicate that the page is loading. Used to encourage donors to
   * stay on the page after client-side payment is complete while they wait
   * for the donation to be submitted to the server.
   */
  loading() {
    this.setState({ loading: true });
  }

  /**
   * Cancel the loading indicator.
   */
  cancelLoading() {
    this.setState({ loading: false });
  }

  /**
   * Called by payment managers when their processing has completed successfully
   *   - navigates to the success page
   */
  succeed() {
    window.location = `/${this.props.config.region_code}/donations/success`;
  }

  /**
   * Called by payment managers when their processing has failed. Updates form
   *   error state and sets `state.submitting` and `state.loading` to `false`
   * @param newErrors relevant error info from the failure, for processing by
   *   `unpackErrors()`
   * @return {boolean}
   */
  fail(newErrors) {
    const newErrorsOrDefault = this.unpackErrors(newErrors);
    this.setState((prevState) => {
      const errors = { ...prevState.errors, ...newErrorsOrDefault };
      return { errors, loading: false, submitting: false };
    });
    return false;
  }

  handleChange(newState) {
    this.setState({ ...newState, validating: true });
    this.enqueueValidation(newState);
  }

  /**
   * Debounce validations by collecting all changes into
   * this.pendingValidations, then deferring them.
   */
  enqueueValidation(newState) {
    if (this.validationsTimeout) {
      clearTimeout(this.validationsTimeout);
    }

    this.pendingValidations = { ...this.pendingValidations, ...newState };

    this.validationsTimeout = setTimeout(() => {
      this.validate(this.pendingValidations);
      this.pendingValidations = {};
    }, VALIDATIONS_DELAY);
  }

  validate(newState) {
    const state = { ...this.state, ...newState };

    this.validation.validate(state).then(() => {
      this.setState({
        errors: {},
        valid: true,
        validating: false,
      });
    }).catch((errors) => {
      this.setState({
        errors,
        valid: false,
        validating: false,
      });
    });
  }

  /**
   * Takes `result` and returns something based on it which can be used to set
   *   error fields on the form. If `result`:
   * 1. has an `errors` property, returns the value of that property, or the
   *   first member if the value is an array, e.g.
   *   `{"errors": {"cardNumber": "is invalid"}}` => `{"cardNumber": "is invalid"}`
   *   `{"errors": {"cardNumber": ["is invalid", "is fraudulent"]}}` => `{"cardNumber": "is invalid"}`
   * 2. has no `errors property, but has other properties, it returns `result`
   *   unchanged, e.g. `{"cardNumber": "is invalid"}` => `{"cardNumber": "is invalid"}`
   * 3. is falsey, returns a default error on the appropriate field
   *   given the current payment method, e.g. `null` => `{"paypal": "Default error"}`
   * 4. is an Error, returns a default error as with a falsey value above
   * @param result an error result passed back from a payment processor
   * @return {*} a set of field names and their error messages
   */
  unpackErrors(result) {
    if (!result || result instanceof Error) {
      const errorField = this.state.paymentProvider === "paypal" ? "paypal" : "cardNumber";
      return { [errorField]: i18n.t("donation.message.payment_error") };
    }
    if (result.errors) {
      return mapObject(result.errors, (message) => {
        if (Array.isArray(message)) {
          return message[0];
        }
        return message;
      });
    }
    return result;
  }

  /**
   * The set of form values which the back-end and/or Stripe needs to know about,
   *   representing a completed donation
   * @return {{country: *, lastName: *, amount: *, address: *, city: *, postcode: *,
   *   title: *, giftAid: *, frequency: *, name: *, recaptchaToken: *,
   *   anonymous: boolean, state: *, paypal: boolean, email: *}}
   */
  get donationDetails() {
    const {
      address,
      amount,
      city,
      country,
      countryCode,
      email,
      frequency,
      giftAid,
      lastName,
      name,
      postcode,
      publiclyRecogniseDonor,
      recaptchaToken,
      state,
      title,
    } = this.state;
    const paypal = this.state.paymentProvider === "paypal";

    return {
      address,
      amount,
      anonymous: !publiclyRecogniseDonor, // NOTE: this value is flipped
      city,
      country,
      countryCode,
      email,
      frequency,
      giftAid,
      lastName,
      name,
      paypal,
      postcode,
      recaptchaToken,
      state,
      title,
    };
  }

  /**
   * A public onClick callback for a `PayPalManager` to hook up to a PayPal
   *   button
   */
  onPaypalClick() {
    this.setState({ showErrors: true });
  }

  render() {
    const errors = this.state.showErrors ? this.state.errors : {};
    return (
      <DonationForm
        address={this.state.address}
        amount={this.state.amount}
        cardNumber={this.state.cardNumber}
        city={this.state.city}
        country={this.state.country}
        config={{
          currencySign: this.props.config.region_currency_sign,
          defaultAmounts: this.props.config.default_amounts_in_dollars,
          giftAid: this.props.config.locale === "en-GB",
          homeCountryCode: this.props.config.region_home_country_code,
          initialFrequency: this.initialFrequency,
          locale: this.props.config.locale,
          showAddressInputs: this.showAddressInputs,
          showOptionalAddress: this.showOptionalAddress,
          suggestedAmounts: this.props.config.suggested_amounts_in_dollars,
        }}
        cvv={this.state.cvv}
        email={this.state.email}
        errors={errors}
        expiry={this.state.expiry}
        fieldsActive={!this.state.submitting}
        frequency={this.state.frequency}
        giftAid={this.state.giftAid}
        lastName={this.state.lastName}
        loading={this.state.loading}
        name={this.state.name}
        onChange={this.handleChange}
        onSubmit={this.handleSubmit}
        payPalManager={this.props.payPalManager}
        paymentProvider={this.state.paymentProvider}
        postcode={this.state.postcode}
        publiclyRecogniseDonor={this.state.publiclyRecogniseDonor}
        recaptcha={{
          enabled: this.props.config.recaptcha_enabled,
          instance: this.recaptchaRef,
          onVerify: this.setRecaptchaToken,
          siteKey: this.props.config.recaptcha_siteKey,
        }}
        state={this.state.state}
        stripeManager={this.props.stripeManager}
        submitActive={!this.state.submitting && !this.state.validating}
        title={this.state.title}
        url={this.props.url}
        valid={this.state.valid}
      />
    );
  }
}

const DonationFormManager = ({
   config,
   paypal,
   presets,
   tcDonateGateway,
   url,
 }) => (
   <ElementsConsumer>
     {({ elements, stripe }) => {
       const donationForm = React.createRef();
       const payPalManager = new PayPalManager({
         allowed: config.Paypal_allowed,
         donationForm,
         env: config.Paypal_env,
         paypal,
         tcDonateGateway,
       });
       const stripeManager = new StripeManager({
         allowed: !!config.Stripe_PublishableKey,
         donationForm,
         elements,
         stripe,
         tcDonateGateway,
       });
       return (<UnwrappedDonationFormManager
         config={config}
         elements={elements}
         payPalManager={payPalManager}
         presets={presets}
         ref={donationForm}
         stripeManager={stripeManager}
         tcDonateGateway={tcDonateGateway}
         url={url}
       />);
     }}
   </ElementsConsumer>
);

const sharedDefaultProps = {
  config: ConfigDefaultProps,
  presets: new Presets(),
  tcDonateGateway: new TcDonateGateway({
    authenticityToken: "proper-auth-token",
    basePath: "/au",
  }),
  url: "/donations",
};

DonationFormManager.defaultProps = {
  paypal: undefined,
  ...sharedDefaultProps,
};

UnwrappedDonationFormManager.defaultProps = {
  payPalManager: new PayPalManager({ }),
  stripeManager: new StripeManager({ stripe: { } }),
  ...sharedDefaultProps,
};

const sharedPropTypes = {
  config: ConfigProps,
  presets: PropTypes.instanceOf(Presets),
  tcDonateGateway: PropTypes.instanceOf(TcDonateGateway),
  url: PropTypes.string,
};

DonationFormManager.propTypes = {
  paypal: PropTypes.shape({
    Buttons: PropTypes.func,
    request: PropTypes.object,
  }),
  ...sharedPropTypes,
};

UnwrappedDonationFormManager.propTypes = {
  payPalManager: PropTypes.instanceOf(PayPalManager),
  stripeManager: PropTypes.instanceOf(StripeManager),
  ...sharedPropTypes,
};

export default DonationFormManager;
